/**
 * Suppliers — dedicated supplier management page.
 *
 * Page composition:
 *   - PageHeader + "Add supplier" action
 *   - KPI strip (Total / Active / Total Outstanding / Top Supplier)
 *   - Filter row (Status toggle, Country dropdown, Search)
 *   - DataTable with row click -> detail Sheet
 *   - Create / Edit Sheet using the shared SupplierForm component
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, Pencil, Plus, Search, Truck } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatRelative } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  SupplierForm,
  type SupplierFormValues,
} from "../../../components/forms/SupplierForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockSuppliers,
  mockPurchaseOrders,
  type MockImportSupplier,
  type MockImportPurchaseOrder,
} from "../../../components/imports/importsMockData";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import { PoStatusBadge } from "../../../components/imports/PoStatusBadge";

export const Route = createFileRoute("/_app/imports/suppliers")({
  component: SuppliersPage,
});

/* ─────────────────────────────────────────────────────────────
 * Types
 * ───────────────────────────────────────────────────────────── */

type SupplierRow = MockImportSupplier & {
  openingBalanceBaseCents?: bigint;
  openingBalanceAsOf?: number | null;
};

type StatusFilter = "active" | "inactive";

const COUNTRY_OPTIONS = [
  { code: "CN", name: "China" },
  { code: "DE", name: "Germany" },
  { code: "BD", name: "Bangladesh" },
  { code: "IN", name: "India" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "South Korea" },
] as const;

const COUNTRY_NAMES: Record<string, string> = Object.fromEntries(
  COUNTRY_OPTIONS.map((c) => [c.code, c.name]),
);

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

function formatDate(ms: number | null): string {
  if (ms === null) return "--";
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/* ─────────────────────────────────────────────────────────────
 * Page component
 * ───────────────────────────────────────────────────────────── */

function SuppliersPage() {
  const isMock = useMockData();

  const [rows, setRows] = usePersistentMockState<SupplierRow[]>(
    "suppliers",
    mockSuppliers as SupplierRow[],
  );
  const [purchaseOrders] = usePersistentMockState<MockImportPurchaseOrder[]>(
    "purchaseOrders",
    mockPurchaseOrders,
  );

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [countryFilter, setCountryFilter] = useState<string | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<SupplierRow | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<SupplierRow | null>(
    null,
  );

  /* ── PO-derived data per supplier ── */
  const poBySupplier = useMemo(() => {
    const m = new Map<
      string,
      {
        outstandingCents: bigint;
        totalPurchaseCents: bigint;
        poCount: number;
        lastPoDate: number | null;
      }
    >();
    for (const po of purchaseOrders) {
      if (po.deletedAt !== null) continue;
      const existing = m.get(po.supplierId) ?? {
        outstandingCents: 0n,
        totalPurchaseCents: 0n,
        poCount: 0,
        lastPoDate: null,
      };
      existing.totalPurchaseCents += po.totalAmountInBaseCents;
      existing.poCount += 1;
      if (
        po.status !== "closed" &&
        po.status !== "received" &&
        po.status !== "cancelled"
      ) {
        existing.outstandingCents += po.totalAmountInBaseCents;
      }
      if (
        existing.lastPoDate === null ||
        po.orderDate > existing.lastPoDate
      ) {
        existing.lastPoDate = po.orderDate;
      }
      m.set(po.supplierId, existing);
    }
    return m;
  }, [purchaseOrders]);

  /* ── KPIs ── */
  const kpis = useMemo(() => {
    const nonDeleted = rows.filter((r) => r.deletedAt === null);
    const activeCount = nonDeleted.filter((r) => r.isActive).length;
    let totalOutstanding = 0n;
    let topSupplierName: string | null = null;
    let topSupplierVolume = 0n;
    for (const s of nonDeleted) {
      const data = poBySupplier.get(s._id);
      if (data) {
        totalOutstanding += data.outstandingCents;
        if (data.totalPurchaseCents > topSupplierVolume) {
          topSupplierVolume = data.totalPurchaseCents;
          topSupplierName = s.displayName;
        }
      }
    }
    return {
      total: nonDeleted.length,
      activeCount,
      totalOutstanding,
      topSupplierName,
    };
  }, [rows, poBySupplier]);

  /* ── Filtering ── */
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (r.deletedAt !== null) return false;
      if (statusFilter === "active" && !r.isActive) return false;
      if (statusFilter === "inactive" && r.isActive) return false;
      if (countryFilter !== "all" && r.countryCode !== countryFilter) {
        return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesName = r.displayName.toLowerCase().includes(q);
        const matchesCode = r.code.toLowerCase().includes(q);
        const matchesContact =
          r.contactPersonName?.toLowerCase().includes(q) ?? false;
        const matchesEmail =
          r.contactEmail?.toLowerCase().includes(q) ?? false;
        if (!matchesName && !matchesCode && !matchesContact && !matchesEmail) {
          return false;
        }
      }
      return true;
    });
  }, [rows, statusFilter, countryFilter, searchQuery]);

  /* ── Distinct countries present in the data ── */
  const countriesInData = useMemo(() => {
    const codes = new Set(
      rows.filter((r) => r.deletedAt === null).map((r) => r.countryCode),
    );
    return COUNTRY_OPTIONS.filter((c) => codes.has(c.code));
  }, [rows]);

  /* ── Columns ── */
  const columns = useMemo<ColumnDef<SupplierRow>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Code",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.code}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-(--text-primary)">
              {row.original.displayName}
            </span>
            {row.original.legalName ? (
              <span className="text-[11px] text-(--text-tertiary)">
                {row.original.legalName}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: "contact",
        header: "Contact",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-(--text-primary)">
              {row.original.contactPersonName ?? "--"}
            </span>
            {row.original.contactEmail ? (
              <span className="text-[11px] text-(--text-tertiary)">
                {row.original.contactEmail}
              </span>
            ) : null}
          </div>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "phone",
        header: "Phone",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">
            {row.original.contactPhone ?? "--"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "country",
        header: "Country",
        cell: ({ row }) => (
          <Badge variant="neutral">
            {COUNTRY_NAMES[row.original.countryCode] ??
              row.original.countryCode}
          </Badge>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "outstanding",
        header: "Outstanding",
        cell: ({ row }) => {
          const data = poBySupplier.get(row.original._id);
          const amt = data?.outstandingCents ?? 0n;
          return (
            <span className="font-mono tabular-nums text-(--text-primary)">
              {formatMoney(amt, "BDT", { locale: "en-BD" })}
            </span>
          );
        },
        meta: { numeric: true, mono: true },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) =>
          row.original.isActive ? (
            <StatusPill variant="success" size="sm">
              Active
            </StatusPill>
          ) : (
            <StatusPill variant="neutral" size="sm">
              Inactive
            </StatusPill>
          ),
      },
      {
        id: "lastPo",
        header: "Last PO",
        cell: ({ row }) => {
          const data = poBySupplier.get(row.original._id);
          return (
            <span className="text-(--text-secondary) tabular-nums">
              {data?.lastPoDate ? formatDate(data.lastPoDate) : "--"}
            </span>
          );
        },
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "updatedAt",
        header: "Updated",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatRelative(row.original.updatedAt)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
    ],
    [poBySupplier],
  );

  /* ── Mutations ── */
  function handleCreate(values: SupplierFormValues) {
    const now = Date.now();
    const newSupplier: SupplierRow = {
      _id: `sup_local_${now}`,
      code: values.code,
      displayName: values.displayName,
      legalName: values.legalName || null,
      countryCode: values.countryCode,
      defaultCurrency: values.defaultCurrency,
      contactPersonName: values.contactPersonName || null,
      contactEmail: values.contactEmail || null,
      contactPhone: values.contactPhone || null,
      addressJson: null,
      taxRegistrationNumber: null,
      bankDetailsJson: null,
      paymentTermsDays: values.paymentTermsDays,
      isActive: true,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      openingBalanceBaseCents: values.openingBalanceBaseCents,
      openingBalanceAsOf: values.openingBalanceAsOf,
    };
    setRows((prev) => [newSupplier, ...prev]);
    setCreateOpen(false);
    toast.success(`Supplier ${newSupplier.code} created (mock)`);
  }

  function handleEdit(values: SupplierFormValues) {
    if (!editingSupplier) return;
    const now = Date.now();
    setRows((prev) =>
      prev.map((s) =>
        s._id === editingSupplier._id
          ? {
              ...s,
              displayName: values.displayName,
              legalName: values.legalName || null,
              countryCode: values.countryCode,
              defaultCurrency: values.defaultCurrency,
              paymentTermsDays: values.paymentTermsDays,
              contactPersonName: values.contactPersonName || null,
              contactEmail: values.contactEmail || null,
              contactPhone: values.contactPhone || null,
              openingBalanceBaseCents: values.openingBalanceBaseCents,
              openingBalanceAsOf: values.openingBalanceAsOf,
              updatedAt: now,
            }
          : s,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === editingSupplier._id
        ? {
            ...prev,
            displayName: values.displayName,
            updatedAt: now,
          }
        : prev,
    );
    setEditingSupplier(null);
    toast.success(
      `Supplier ${editingSupplier.code} updated. Audit trail recorded.`,
    );
  }

  function handleToggleActive(supplier: SupplierRow) {
    const now = Date.now();
    const nextActive = !supplier.isActive;
    setRows((prev) =>
      prev.map((s) =>
        s._id === supplier._id
          ? { ...s, isActive: nextActive, updatedAt: now }
          : s,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === supplier._id
        ? { ...prev, isActive: nextActive, updatedAt: now }
        : prev,
    );
    toast.success(
      `${supplier.displayName} ${nextActive ? "activated" : "deactivated"}.`,
    );
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const cols: ExportColumn<SupplierRow>[] = [
      { key: "code", label: "Code" },
      { key: "displayName", label: "Display name" },
      {
        key: "legalName",
        label: "Legal name",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "countryCode",
        label: "Country",
        value: (r) => COUNTRY_NAMES[r.countryCode] ?? r.countryCode,
      },
      { key: "defaultCurrency", label: "Default currency" },
      { key: "paymentTermsDays", label: "Payment terms (days)" },
      {
        key: "contactPersonName",
        label: "Contact person",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "contactEmail",
        label: "Email",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "contactPhone",
        label: "Phone",
        format: (v) => (v == null ? "" : String(v)),
      },
      { key: "isActive", label: "Active" },
      {
        key: "createdAt",
        label: "Created",
        format: (v) => formatDateForCsv(Number(v)),
      },
      {
        key: "updatedAt",
        label: "Updated",
        format: (v) => formatDateForCsv(Number(v)),
      },
    ];
    downloadCsv(
      exportFilename("suppliers", extension),
      toCsv(filtered, cols),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Suppliers"
        description="Manage your import suppliers, contacts, and outstanding payables."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add supplier
            </Button>
          </>
        }
      />

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Supplier summary"
      >
        <KpiCard
          caption="Total suppliers"
          value={kpis.total}
          hint="Excluding deleted"
        />
        <KpiCard
          caption="Active suppliers"
          value={kpis.activeCount}
          hint="Currently active"
        />
        <KpiCard
          caption="Total outstanding"
          value={formatMoneySmart(kpis.totalOutstanding, "BDT", {
            locale: "en-BD",
          })}
          hint="Open PO value in BDT"
        />
        <KpiCard
          caption="Top supplier"
          value={kpis.topSupplierName ?? "--"}
          hint="By total purchase volume"
        />
      </section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-(--text-tertiary)" />
          <Input
            placeholder="Search name, code, contact..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 w-56 text-[13px]"
          />
        </div>
        {countriesInData.length > 1 ? (
          <FilterMenu
            label="Country"
            options={[
              { value: "all", label: "All countries" },
              ...countriesInData.map((c) => ({
                value: c.code,
                label: c.name,
              })),
            ]}
            value={countryFilter}
            onChange={(v) => setCountryFilter(v)}
          />
        ) : null}
        <div className="inline-flex items-center rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-0.5">
          <ToggleChip
            active={statusFilter === "active"}
            onClick={() => setStatusFilter("active")}
          >
            Active
          </ToggleChip>
          <ToggleChip
            active={statusFilter === "inactive"}
            onClick={() => setStatusFilter("inactive")}
          >
            Inactive
          </ToggleChip>
        </div>
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No suppliers match"
        emptyDescription={
          rows.filter((r) => r.deletedAt === null).length === 0
            ? "Add your first supplier to start managing import relationships."
            : "Try widening the filters."
        }
        emptyIcon={<Truck className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.filter((r) => r.deletedAt === null).length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add supplier
            </Button>
          ) : undefined
        }
      />

      {/* Create Sheet */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>New supplier</SheetTitle>
            <SheetDescription>
              Onboard a new import supplier. Code must be unique.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <SupplierForm
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail / Edit Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setEditingSupplier(null);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow && editingSupplier ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingSupplier.code}</SheetTitle>
                <SheetDescription>
                  Update supplier details and opening AP balance.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <SupplierForm
                  isEdit
                  initialValues={{
                    code: editingSupplier.code,
                    displayName: editingSupplier.displayName,
                    legalName: editingSupplier.legalName ?? "",
                    countryCode: editingSupplier.countryCode,
                    defaultCurrency: editingSupplier.defaultCurrency,
                    paymentTermsDays: editingSupplier.paymentTermsDays,
                    contactPersonName:
                      editingSupplier.contactPersonName ?? "",
                    contactEmail: editingSupplier.contactEmail ?? "",
                    contactPhone: editingSupplier.contactPhone ?? "",
                    openingBalanceBaseCents:
                      editingSupplier.openingBalanceBaseCents ?? 0n,
                    openingBalanceAsOf:
                      editingSupplier.openingBalanceAsOf ?? null,
                  }}
                  onSubmit={handleEdit}
                  onCancel={() => setEditingSupplier(null)}
                />
              </SheetBody>
            </>
          ) : detailRow ? (
            <SupplierDetailPanel
              row={detailRow}
              purchaseOrders={purchaseOrders}
              poData={poBySupplier.get(detailRow._id) ?? null}
              onEdit={() => setEditingSupplier(detailRow)}
              onToggleActive={() => handleToggleActive(detailRow)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail panel
 * ───────────────────────────────────────────────────────────── */

function SupplierDetailPanel({
  row,
  purchaseOrders,
  poData,
  onEdit,
  onToggleActive,
}: {
  row: SupplierRow;
  purchaseOrders: MockImportPurchaseOrder[];
  poData: {
    outstandingCents: bigint;
    totalPurchaseCents: bigint;
    poCount: number;
    lastPoDate: number | null;
  } | null;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  const [showAllPos, setShowAllPos] = useState(false);

  const supplierPos = useMemo(
    () =>
      purchaseOrders
        .filter(
          (po) => po.supplierId === row._id && po.deletedAt === null,
        )
        .sort((a, b) => b.orderDate - a.orderDate),
    [purchaseOrders, row._id],
  );
  const visiblePos = showAllPos ? supplierPos : supplierPos.slice(0, 10);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span>{row.displayName}</span>
          {row.isActive ? (
            <StatusPill variant="success" size="sm">
              Active
            </StatusPill>
          ) : (
            <StatusPill variant="neutral" size="sm">
              Inactive
            </StatusPill>
          )}
          <RecordAuditBadge tableName="suppliers" recordId={row._id} />
        </SheetTitle>
        <SheetDescription>
          <span className="font-mono text-[12px]">{row.code}</span>
          {row.legalName ? ` -- ${row.legalName}` : null}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="orders">Purchase Orders</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
              {/* Contact */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                  Contact Information
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <DetailField
                    label="Contact person"
                    value={row.contactPersonName ?? "--"}
                  />
                  <DetailField
                    label="Phone"
                    value={row.contactPhone ?? "--"}
                  />
                  <DetailField
                    label="Email"
                    value={row.contactEmail ?? "--"}
                  />
                  <DetailField
                    label="Country"
                    value={
                      COUNTRY_NAMES[row.countryCode] ?? row.countryCode
                    }
                  />
                </div>
              </section>

              {/* Trade terms */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                  Trade Terms
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <DetailField
                    label="Default currency"
                    value={
                      <Badge variant="neutral" className="font-mono">
                        {row.defaultCurrency}
                      </Badge>
                    }
                  />
                  <DetailField
                    label="Payment terms"
                    value={`${row.paymentTermsDays} days`}
                  />
                  <DetailField
                    label="Tax reg. number"
                    value={row.taxRegistrationNumber ?? "--"}
                  />
                </div>
              </section>

              {/* Purchase summary */}
              <section className="rounded-[var(--radius-md)] border border-(--border-subtle) p-3 flex flex-col gap-2">
                <div className="text-[12px] text-(--text-tertiary) uppercase tracking-wide">
                  Purchase Summary
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <DetailField
                    label="Total POs"
                    value={String(poData?.poCount ?? 0)}
                  />
                  <DetailField
                    label="Total purchased"
                    value={formatMoney(
                      poData?.totalPurchaseCents ?? 0n,
                      "BDT",
                      { locale: "en-BD" },
                    )}
                  />
                  <DetailField
                    label="Outstanding"
                    value={
                      <span
                        className={
                          (poData?.outstandingCents ?? 0n) > 0n
                            ? "text-[var(--warning)]"
                            : undefined
                        }
                      >
                        {formatMoney(
                          poData?.outstandingCents ?? 0n,
                          "BDT",
                          { locale: "en-BD" },
                        )}
                      </span>
                    }
                  />
                  <DetailField
                    label="Last PO"
                    value={
                      poData?.lastPoDate
                        ? formatDate(poData.lastPoDate)
                        : "--"
                    }
                  />
                </div>
              </section>

              {/* Opening balance */}
              {(row.openingBalanceBaseCents ?? 0n) !== 0n ? (
                <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                    Opening AP Balance
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <DetailField
                      label="Amount"
                      value={formatMoney(
                        row.openingBalanceBaseCents ?? 0n,
                        "BDT",
                        { locale: "en-BD" },
                      )}
                    />
                    <DetailField
                      label="As of"
                      value={formatDate(row.openingBalanceAsOf ?? null)}
                    />
                  </div>
                </section>
              ) : null}

              {/* Metadata */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                  Record Info
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <DetailField
                    label="Created"
                    value={formatDate(row.createdAt)}
                  />
                  <DetailField
                    label="Last updated"
                    value={formatDate(row.updatedAt)}
                  />
                </div>
              </section>
            </div>
          </TabsContent>

          {/* Purchase Orders */}
          <TabsContent value="orders" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Purchase Orders ({supplierPos.length})
              </div>
              {supplierPos.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No purchase orders for this supplier.
                </div>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {visiblePos.map((po) => (
                      <li
                        key={po._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="font-mono text-[11px]">
                            {po.poNumber}
                          </span>
                          <PoStatusBadge status={po.status} />
                          <span className="text-[11px] text-(--text-tertiary)">
                            {formatDate(po.orderDate)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono tabular-nums text-[13px]">
                            {formatMoney(
                              po.totalAmountInBaseCents,
                              "BDT",
                              { locale: "en-BD" },
                            )}
                          </span>
                          <Badge variant="neutral" className="font-mono">
                            {po.currency}
                          </Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {supplierPos.length > 10 && !showAllPos ? (
                    <button
                      type="button"
                      onClick={() => setShowAllPos(true)}
                      className="text-[12px] text-(--accent) hover:underline self-start"
                    >
                      Show all {supplierPos.length} purchase orders
                    </button>
                  ) : null}
                </>
              )}
              {/* Totals */}
              {supplierPos.length > 0 ? (
                <div className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-canvas) p-3 grid grid-cols-2 gap-3">
                  <DetailField
                    label="Total PO value"
                    value={formatMoney(
                      supplierPos.reduce(
                        (s, po) => s + po.totalAmountInBaseCents,
                        0n,
                      ),
                      "BDT",
                      { locale: "en-BD" },
                    )}
                  />
                  <DetailField
                    label="Open PO value"
                    value={formatMoney(
                      supplierPos
                        .filter(
                          (po) =>
                            po.status !== "closed" &&
                            po.status !== "received" &&
                            po.status !== "cancelled",
                        )
                        .reduce(
                          (s, po) => s + po.totalAmountInBaseCents,
                          0n,
                        ),
                      "BDT",
                      { locale: "en-BD" },
                    )}
                  />
                </div>
              ) : null}
            </div>
          </TabsContent>

          {/* History */}
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="suppliers" recordId={row._id} />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          aria-label="Edit"
        >
          <Pencil className="size-4" />
        </Button>
        <Button
          variant={row.isActive ? "outline" : "default"}
          onClick={onToggleActive}
        >
          {row.isActive ? "Deactivate" : "Activate"}
        </Button>
      </SheetFooter>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Shared UI atoms (same pattern as dealers.tsx)
 * ───────────────────────────────────────────────────────────── */

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}

function ToggleChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 text-[12px] rounded-md transition-colors ${active ? "bg-(--bg-selected) text-(--text-primary)" : "text-(--text-secondary) hover:text-(--text-primary)"}`}
    >
      {children}
    </button>
  );
}

function FilterMenu({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <span className="text-(--text-tertiary)">{label}:</span>
          <span className="text-(--text-primary)">
            {current?.label ?? "--"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-(--text-tertiary)" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
